import { useQuery, useMutation } from "@tanstack/react-query";
import { useParams } from "wouter";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SearchableSelect } from "@/lib/picker-search";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Target, Plus, Search, Sparkles } from "lucide-react";
import { useState } from "react";
import type { StrategyNode, Persona, Product, AdUnitType, Offer } from "@shared/schema";
import { apiRequest, queryClient, getQueryFn } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";

const statusColors: Record<string, "default" | "secondary" | "destructive"> = {
  idea: "secondary",
  approved: "default",
  testing: "default",
  winner: "default",
  loser: "destructive",
  archived: "secondary",
};

const funnelLabels: Record<string, string> = {
  tof: "Top of Funnel",
  mof: "Middle of Funnel",
  bof: "Bottom of Funnel",
  retention: "Retention",
};

export default function StrategyMatrixPage() {
  const { brandId } = useParams<{ brandId: string }>();
  const { toast } = useToast();
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState<Record<string, string>>({});

  const { data: nodes, isLoading } = useQuery<StrategyNode[]>({
    queryKey: [`/api/brands/${brandId}/strategy-nodes`],
    enabled: !!brandId,
  });
  const { data: personas } = useQuery<Persona[]>({
    queryKey: [`/api/brands/${brandId}/personas`],
    enabled: !!brandId,
  });
  const { data: products } = useQuery<Product[]>({
    queryKey: [`/api/brands/${brandId}/products`],
    enabled: !!brandId,
  });
  const { data: adUnits } = useQuery<AdUnitType[]>({
    queryKey: [`/api/brands/${brandId}/ad-units`],
    enabled: !!brandId,
  });
  const { data: offers } = useQuery<Offer[]>({
    queryKey: [`/api/brands/${brandId}/offers`],
    enabled: !!brandId,
  });

  const personaMap = new Map(personas?.map(p => [p.id, p.name]) || []);
  const productMap = new Map(products?.map(p => [p.id, p.name]) || []);
  const adUnitMap = new Map(adUnits?.map(a => [a.id, a.name]) || []);
  const offerMap = new Map(offers?.map(o => [o.id, o.name]) || []);

  const createMutation = useMutation({
    mutationFn: async (data: Record<string, string>) => {
      const res = await apiRequest("POST", `/api/brands/${brandId}/strategy-nodes`, {
        ...data,
        priorityScore: parseInt(data.priorityScore || "50"),
        confidenceScore: data.confidenceScore || "0.5",
      });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/brands/${brandId}/strategy-nodes`] });
      setOpen(false);
      setFormData({});
      toast({ title: "Strategy node created" });
    },
    onError: (e: Error) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const aiMutation = useMutation({
    mutationFn: async ({ nodeId, jobType }: { nodeId: string; jobType: string }) => {
      const res = await apiRequest("POST", "/api/ai/jobs", { brandId, strategyNodeId: nodeId, jobType });
      return res.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: [`/api/brands/${brandId}/outputs`] });
      toast({ title: "AI job completed", description: data.output?.title || "Output generated" });
    },
    onError: (e: Error) => toast({ title: "AI job failed", description: e.message, variant: "destructive" }),
  });

  const filtered = nodes?.filter(n => {
    const matchSearch = n.angleTitle.toLowerCase().includes(search.toLowerCase()) ||
      personaMap.get(n.personaId)?.toLowerCase().includes(search.toLowerCase()) ||
      productMap.get(n.productId)?.toLowerCase().includes(search.toLowerCase());
    const matchStatus = statusFilter === "all" || n.status === statusFilter;
    return matchSearch && matchStatus;
  }) || [];

  return (
    <div className="p-8 max-w-6xl mx-auto space-y-6">
      <div className="flex items-center justify-between gap-4 flex-wrap fade-in-up">
        <div>
          <h1 className="text-2xl font-bold tracking-tight" data-testid="text-matrix-title">Strategy Matrix</h1>
          <p className="text-sm text-muted-foreground mt-0.5">Strategy nodes combining persona + product + ad unit</p>
        </div>
        <Button onClick={() => setOpen(true)} className="rounded-full px-5" data-testid="button-create-node">
          <Plus className="h-4 w-4 mr-1.5" /> New Node
        </Button>
      </div>

      <div className="flex items-center gap-3 flex-wrap fade-in-up-delay-1">
        <div className="relative max-w-sm flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Search nodes..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9 rounded-full" data-testid="input-search-nodes" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-36" data-testid="select-status-filter">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="idea">Idea</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="testing">Testing</SelectItem>
            <SelectItem value="winner">Winner</SelectItem>
            <SelectItem value="loser">Loser</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {isLoading ? (
        <div className="space-y-4">{[1, 2, 3].map(i => <Skeleton key={i} className="h-32" />)}</div>
      ) : filtered.length > 0 ? (
        <div className="space-y-3">
          {filtered.map(node => (
            <Card key={node.id} className="premium-card" data-testid={`card-node-${node.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0 space-y-2">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-semibold text-sm">{node.angleTitle}</h3>
                      <Badge variant={statusColors[node.status] || "secondary"} className="text-xs capitalize">{node.status}</Badge>
                      {node.claimRisk && node.claimRisk !== "green" && (
                        <Badge variant={node.claimRisk === "red" ? "destructive" : "secondary"} className="text-xs">
                          {node.claimRisk} risk
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-3 flex-wrap text-xs text-muted-foreground">
                      <span className="font-medium text-foreground">{personaMap.get(node.personaId) || "Unknown"}</span>
                      <span>x</span>
                      <span className="font-medium text-foreground">{productMap.get(node.productId) || "Unknown"}</span>
                      <span>x</span>
                      <span className="font-medium text-foreground">{adUnitMap.get(node.adUnitTypeId) || "Unknown"}</span>
                      {node.offerId && (
                        <>
                          <span>+</span>
                          <span className="font-medium text-foreground">{offerMap.get(node.offerId) || "Offer"}</span>
                        </>
                      )}
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge variant="secondary" className="text-xs">{funnelLabels[node.funnelStage] || node.funnelStage}</Badge>
                      <Badge variant="secondary" className="text-xs capitalize">{node.awarenessStage?.replace(/_/g, " ")}</Badge>
                      <span className="text-xs text-muted-foreground">Priority: {node.priorityScore}</span>
                      <span className="text-xs text-muted-foreground">Confidence: {Number(node.confidenceScore) * 100}%</span>
                    </div>
                    {node.angleSummary && (
                      <p className="text-xs text-muted-foreground line-clamp-2">{node.angleSummary}</p>
                    )}
                  </div>
                  <div className="flex flex-col gap-1 shrink-0">
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => aiMutation.mutate({ nodeId: node.id, jobType: "strategy_brief" })}
                      disabled={aiMutation.isPending}
                      data-testid={`button-generate-${node.id}`}
                    >
                      <Sparkles className="h-3 w-3 mr-1" /> Generate
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Target className="h-10 w-10 text-muted-foreground mb-3" />
            <h3 className="font-medium mb-1">No strategy nodes</h3>
            <p className="text-sm text-muted-foreground">Create your first strategy node to start building</p>
          </CardContent>
        </Card>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Create Strategy Node</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Persona *</Label>
              <SearchableSelect
                value={formData.personaId || ""}
                onValueChange={v => setFormData(p => ({ ...p, personaId: v }))}
                placeholder="Select persona..."
                testId="select-persona"
                options={(personas ?? []).map(p => ({ value: p.id, label: p.name }))}
              />
            </div>
            <div className="space-y-2">
              <Label>Product *</Label>
              <SearchableSelect
                value={formData.productId || ""}
                onValueChange={v => setFormData(p => ({ ...p, productId: v }))}
                placeholder="Select product..."
                testId="select-product"
                options={(products ?? []).map(p => ({ value: p.id, label: p.name }))}
              />
            </div>
            <div className="space-y-2">
              <Label>Ad Unit *</Label>
              <SearchableSelect
                value={formData.adUnitTypeId || ""}
                onValueChange={v => setFormData(p => ({ ...p, adUnitTypeId: v }))}
                placeholder="Select ad unit..."
                testId="select-ad-unit"
                options={(adUnits ?? []).map(a => ({ value: a.id, label: a.name }))}
              />
            </div>
            <div className="space-y-2">
              <Label>Offer (optional)</Label>
              <SearchableSelect
                value={formData.offerId || ""}
                onValueChange={v => setFormData(p => ({ ...p, offerId: v }))}
                placeholder="Select offer..."
                testId="select-offer"
                options={(offers ?? []).map(o => ({ value: o.id, label: o.name }))}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Funnel Stage *</Label>
                <Select value={formData.funnelStage || ""} onValueChange={v => setFormData(p => ({ ...p, funnelStage: v }))}>
                  <SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="tof">Top of Funnel</SelectItem>
                    <SelectItem value="mof">Middle of Funnel</SelectItem>
                    <SelectItem value="bof">Bottom of Funnel</SelectItem>
                    <SelectItem value="retention">Retention</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Awareness Stage *</Label>
                <Select value={formData.awarenessStage || ""} onValueChange={v => setFormData(p => ({ ...p, awarenessStage: v }))}>
                  <SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unaware">Unaware</SelectItem>
                    <SelectItem value="problem_aware">Problem Aware</SelectItem>
                    <SelectItem value="solution_aware">Solution Aware</SelectItem>
                    <SelectItem value="product_aware">Product Aware</SelectItem>
                    <SelectItem value="most_aware">Most Aware</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Angle Title *</Label>
              <Input data-testid="input-angle-title" value={formData.angleTitle || ""} onChange={e => setFormData(p => ({ ...p, angleTitle: e.target.value }))} />
            </div>
            <div className="space-y-2">
              <Label>Angle Summary</Label>
              <Textarea value={formData.angleSummary || ""} onChange={e => setFormData(p => ({ ...p, angleSummary: e.target.value }))} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Enemy</Label>
                <Textarea value={formData.enemy || ""} onChange={e => setFormData(p => ({ ...p, enemy: e.target.value }))} rows={2} />
              </div>
              <div className="space-y-2">
                <Label>Promise</Label>
                <Textarea value={formData.promise || ""} onChange={e => setFormData(p => ({ ...p, promise: e.target.value }))} rows={2} />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Main Objection</Label>
              <Input value={formData.mainObjection || ""} onChange={e => setFormData(p => ({ ...p, mainObjection: e.target.value }))} />
            </div>
            <div className="space-y-2">
              <Label>Hypothesis</Label>
              <Textarea value={formData.hypothesis || ""} onChange={e => setFormData(p => ({ ...p, hypothesis: e.target.value }))} />
            </div>
            <div className="space-y-2">
              <Label>CTA Type</Label>
              <Select value={formData.ctaType || "shop_now"} onValueChange={v => setFormData(p => ({ ...p, ctaType: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="learn_more">Learn More</SelectItem>
                  <SelectItem value="shop_now">Shop Now</SelectItem>
                  <SelectItem value="bundle_up">Bundle Up</SelectItem>
                  <SelectItem value="try_it">Try It</SelectItem>
                  <SelectItem value="compare">Compare</SelectItem>
                  <SelectItem value="subscribe">Subscribe</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setOpen(false)}>Cancel</Button>
            <Button
              onClick={() => createMutation.mutate(formData)}
              disabled={!formData.personaId || !formData.productId || !formData.adUnitTypeId || !formData.funnelStage || !formData.awarenessStage || !formData.angleTitle || createMutation.isPending}
              data-testid="button-submit-node"
            >
              {createMutation.isPending ? "Creating..." : "Create Node"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
